class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.backdrop     = document.getElementById('cart-notification-backdrop');
    this.header       = document.querySelector('sticky-header');
    this.onBodyClick  = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    if (this.backdrop) this.backdrop.addEventListener('click', this.close.bind(this));

    this.querySelectorAll('.cart-notification__close, .cart-notification__continue').forEach((btn) =>
      btn.addEventListener('click', this.close.bind(this))
    );

    /* Intercept "Order Now" upsell form submissions */
    this.notification.addEventListener('submit', this.handleUpsellAdd.bind(this));

    /* Intercept clicks on the cart-thumb remove buttons */
    this.notification.addEventListener('click', this.handleRemoveClick.bind(this));
  }

  open() {
    this.notification.classList.add('animate', 'active');
    if (this.backdrop) this.backdrop.classList.add('active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.notification.classList.remove('active');
    if (this.backdrop) this.backdrop.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);
    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;

    /* Update cart icon bubble, "View cart" button AND the cart-drawer from
       the sections payload that came back with /cart/add.js */
    if (parsedState.sections) {
      ['cart-notification-button', 'cart-icon-bubble'].forEach((id) => {
        const el = document.getElementById(id);
        if (!el || !parsedState.sections[id]) return;
        el.innerHTML = this.getSectionInnerHTML(parsedState.sections[id]);
      });

      /* Cart-drawer: vervang de inner content (#CartDrawer) zodat de outer
         <cart-drawer> custom element + z'n JS handlers behouden blijven. */
      if (parsedState.sections['cart-drawer']) {
        this.updateCartDrawerFromHtml(parsedState.sections['cart-drawer']);
      }
    }

    /* Fetch the full cart and render all items as thumbnails in the left column */
    fetch('/cart.js')
      .then((r) => r.json())
      .then((cart) => {
        this.renderCartItems(cart, parsedState.key);
        /* update bubble count when sections were not provided */
        if (!parsedState.sections) {
          const bubble = document.querySelector('.cart-count-bubble span[aria-hidden]');
          if (bubble) bubble.textContent = cart.item_count;
        }
        if (this.header) this.header.reveal();
        this.open();
      })
      .catch(() => {
        if (this.header) this.header.reveal();
        this.open();
      });
  }

  /* Helper: take a "cart-drawer" section HTML string and inject it into the
     existing cart-drawer DOM without re-creating the custom element. */
  updateCartDrawerFromHtml(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const freshInner  = parsed.querySelector('#CartDrawer');
    const liveInner   = document.querySelector('#CartDrawer');
    if (freshInner && liveInner) {
      liveInner.innerHTML = freshInner.innerHTML;
    }
    /* Sync is-empty class on the outer custom element */
    const freshOuter = parsed.querySelector('cart-drawer');
    const liveOuter  = document.querySelector('cart-drawer');
    if (freshOuter && liveOuter) {
      liveOuter.className = freshOuter.className;
    }
  }

  /* Render all cart items as horizontal thumbnails in the left column */
  renderCartItems(cart, newItemKey) {
    const container = document.getElementById('cart-notification-product');
    if (!container) return;

    const formatMoney = (cents) =>
      '\u20ac' + (cents / 100).toFixed(2).replace('.', ',');

    const thumbsHtml = cart.items
      .map((item) => {
        const isNew  = newItemKey && item.key === newItemKey;
        const imgSrc = (item.featured_image && item.featured_image.url) || item.image || '';
        const name   = item.product_title || item.title || '';
        const safeName = name.replace(/"/g, '&quot;');
        const safeKey  = String(item.key).replace(/"/g, '&quot;');

        return `<div class="cn-cart-thumb${isNew ? ' cn-cart-thumb--new' : ''}" data-line-key="${safeKey}">
          <div class="cn-cart-thumb__img">
            ${imgSrc
              ? `<img src="${imgSrc}" alt="${safeName}" width="80" height="80" loading="lazy">`
              : ''}
          </div>
          <div class="cn-cart-thumb__info">
            <p class="cn-cart-thumb__name" title="${safeName}">${name}</p>
            <p class="cn-cart-thumb__qty">&times;&nbsp;${item.quantity}</p>
          </div>
          <p class="cn-cart-thumb__price">${formatMoney(item.final_line_price)}</p>
          <button
            type="button"
            class="cn-cart-thumb__remove"
            data-cn-remove
            data-line-key="${safeKey}"
            aria-label="Verwijder ${safeName}"
            title="Verwijder ${safeName}">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>`;
      })
      .join('');

    const count = cart.item_count;
    const total = formatMoney(cart.total_price);

    container.innerHTML = `
      <div class="cn-cart-items">
        <div class="cn-cart-items__scroll">${thumbsHtml}</div>
        <p class="cn-cart-items__summary">
          Je hebt <strong>${count}&nbsp;item${count !== 1 ? 's' : ''}</strong> in je winkelwagen
          &mdash; Totaal:&nbsp;<strong>${total}</strong>
        </p>
      </div>`;

    this.updateGiftSetBar(cart);
  }

  /*
    Update the gift-set progress banner based on the number of pairs in cart.
    < target → shows "Maak het compleet" copy + partial progress
    >= target → shows "Cadeauset is compleet" copy + 100% fill + complete state
    No copy configured for that state → banner is hidden.
  */
  updateGiftSetBar(cart) {
    const bar = document.getElementById('cn-giftset');
    if (!bar) return;

    const target       = parseInt(bar.dataset.target, 10) || 2;
    const progressText = bar.dataset.progressText || '';
    const completeText = bar.dataset.completeText || '';
    const count        = cart.item_count || 0;
    const isComplete   = count >= target;

    const textEl = bar.querySelector('.cn-giftset__text');
    const fillEl = bar.querySelector('.cn-giftset__fill');

    const copy = isComplete ? completeText : progressText;

    if (!copy) {
      bar.hidden = true;
      bar.classList.remove('cn-giftset--complete');
      return;
    }

    bar.hidden = false;
    bar.classList.toggle('cn-giftset--complete', isComplete);

    if (textEl) textEl.textContent = copy;
    if (fillEl) {
      const percent = Math.min(100, Math.round((count / target) * 100));
      /* Set both the CSS variable AND the width fallback, with !important so
         no other rule can suppress it. */
      fillEl.style.setProperty('--cn-fill', percent + '%');
      fillEl.style.setProperty('width', percent + '%', 'important');
    }
  }

  /* AJAX handler for upsell "Toevoegen" buttons.
     Uses Shopify's sections-rendering so cart-drawer + count bubble + view-cart
     button all stay in sync with the cart-notification UI. */
  async handleUpsellAdd(evt) {
    const form = evt.target.closest('.cart-upsell-item__form');
    if (!form) return;
    evt.preventDefault();

    const btn          = form.querySelector('[type="submit"]');
    const originalHtml = btn.innerHTML;
    btn.disabled       = true;
    btn.innerHTML      = '&hellip;';

    const variantId = parseInt(form.querySelector('.upsell-variant-id').value, 10);
    const qty       = parseInt(form.querySelector('.upsell-qty-value').value, 10) || 1;

    try {
      /* Request the rendered HTML for these sections back with the add response.
         This is the same mechanism Dawn's product-form.js uses. */
      const sectionIds = ['cart-notification-button', 'cart-icon-bubble', 'cart-drawer'];
      const formData = new FormData();
      formData.append('id', String(variantId));
      formData.append('quantity', String(qty));
      formData.append('sections', sectionIds.join(','));
      formData.append('sections_url', window.location.pathname);

      const addResp = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
        body:    formData,
      });
      if (!addResp.ok) throw new Error('add failed');

      const addData = await addResp.json();
      const newKey  = addData.key
                   || (addData.items && addData.items[0] && addData.items[0].key);

      /* Apply each returned section to the live DOM */
      if (addData.sections) {
        ['cart-notification-button', 'cart-icon-bubble'].forEach((id) => {
          const html = addData.sections[id];
          if (!html) return;
          const el = document.getElementById(id);
          if (el) el.innerHTML = this.getSectionInnerHTML(html);
        });
        if (addData.sections['cart-drawer']) {
          this.updateCartDrawerFromHtml(addData.sections['cart-drawer']);
        }
      }

      /* Re-render left column with highlighted new item + update giftset bar */
      const cart = await fetch('/cart.js').then((r) => r.json());
      this.renderCartItems(cart, newKey);

      /* Visual confirmation on the button */
      btn.innerHTML = '&#10003; Toegevoegd';
      setTimeout(() => {
        btn.innerHTML = originalHtml;
        btn.disabled  = false;
      }, 1800);
    } catch (err) {
      console.error('[BK] Upsell add-to-cart error:', err);
      btn.innerHTML = originalHtml;
      btn.disabled  = false;
    }
  }

  /* Remove a line from the cart when its × button is clicked.
     Re-renders cart-notification UI + cart-drawer + count bubble + view-cart button. */
  async handleRemoveClick(evt) {
    const btn = evt.target.closest('[data-cn-remove]');
    if (!btn) return;
    evt.preventDefault();
    evt.stopPropagation();

    const lineKey = btn.getAttribute('data-line-key');
    if (!lineKey) return;

    const thumb = btn.closest('.cn-cart-thumb');
    if (thumb) thumb.classList.add('cn-cart-thumb--removing');
    btn.disabled = true;

    try {
      const resp = await fetch('/cart/change.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body:    JSON.stringify({ id: lineKey, quantity: 0 }),
      });
      if (!resp.ok) throw new Error('remove failed');

      const cart = await resp.json();

      /* Re-render thumbs (no newly-added highlight) */
      this.renderCartItems(cart, null);

      /* Refresh notification-button + bubble + cart-drawer via sections API */
      try {
        const sectionsResp = await fetch('/?sections=cart-notification-button,cart-icon-bubble,cart-drawer');
        const sections = await sectionsResp.json();
        ['cart-notification-button', 'cart-icon-bubble'].forEach((id) => {
          if (!sections[id]) return;
          const el = document.getElementById(id);
          if (el) el.innerHTML = this.getSectionInnerHTML(sections[id]);
        });
        if (sections['cart-drawer']) {
          this.updateCartDrawerFromHtml(sections['cart-drawer']);
        }
      } catch (e) { /* swallow — UI fallback below */ }

      /* Auto-close the notification if the cart is now empty */
      if (cart.item_count === 0) {
        setTimeout(() => this.close(), 600);
      }
    } catch (err) {
      console.error('[BK] Cart notification remove error:', err);
      if (thumb) thumb.classList.remove('cn-cart-thumb--removing');
      btn.disabled = false;
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
      {
        id: 'cart-drawer',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);


